'use client';

import { useState, useTransition } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import { Loader2, Check } from 'lucide-react';
import { subscribeNewsletter } from '@/actions/newsletter/subscribe';
import { cn } from '@/lib/utils';

interface Props {
  /** Etiqueta "Newsletter" sobre el heading */
  eyebrow?: string;
  /** Heading grande */
  heading?: string;
  /** Descripción bajo el heading */
  description?: string;
  /** Source para analytics / segmentación */
  source?: 'footer' | 'home' | 'editorial' | 'popup';
  /** Mostrar checkbox opcional "También carta de tarot" */
  showTarotOptIn?: boolean;
  /** Variante compacta (menos vertical rhythm, sin heading) */
  compact?: boolean;
}

export function NewsletterForm({
  eyebrow = 'Newsletter',
  heading = 'Recibe el ritual semanal',
  description = 'Carta del tarot NQORO cada lunes. Lecturas cortas, objetos de culto en primera línea.',
  source = 'footer',
  showTarotOptIn = true,
  compact = false,
}: Props) {
  const [isPending, startTransition] = useTransition();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [tarotOptIn, setTarotOptIn] = useState(false);
  const [done, setDone] = useState<{ alreadyConfirmed: boolean } | null>(null);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      toast.error('Correo inválido');
      return;
    }
    startTransition(async () => {
      const res = await subscribeNewsletter({
        email: trimmed,
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        source,
        tarotOptIn,
      });
      if (!res.ok) {
        toast.error(res.error ?? 'No pudimos procesar tu suscripción');
        return;
      }
      setDone({ alreadyConfirmed: !!res.alreadyConfirmed });
    });
  }

  if (done) {
    return (
      <div className={cn(compact ? 'text-left' : 'text-center')}>
        <div className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-success)]/15 text-[var(--color-success)]">
          <Check className="h-5 w-5" />
        </div>
        <p className="type-body text-[var(--color-fg)]">
          {done.alreadyConfirmed
            ? 'Ya estabas suscrito. ¡Gracias!'
            : 'Revisa tu correo para confirmar la suscripción.'}
        </p>
        {!done.alreadyConfirmed ? (
          <p className="type-caption mt-2 text-[var(--color-fg-muted)]">
            Si no llega en 5 minutos, revisa la carpeta de spam.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'text-center'}>
      {!compact ? (
        <>
          <p className="type-eyebrow mb-4 text-[var(--color-fg-subtle)]">{eyebrow}</p>
          <h2 className="type-h2 mb-4 text-[var(--color-fg)]">{heading}</h2>
          <p className="type-body-lg mb-8 text-[var(--color-fg-muted)]">{description}</p>
        </>
      ) : null}

      <form
        onSubmit={onSubmit}
        className={cn('flex flex-col gap-3 sm:flex-row', !compact && 'sm:justify-center')}
      >
        <label htmlFor={`newsletter-email-${source}`} className="sr-only">
          Correo electrónico
        </label>
        <input
          id={`newsletter-email-${source}`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@correo.com"
          autoComplete="email"
          disabled={isPending}
          className="h-12 w-full max-w-sm rounded-sm border border-[var(--color-border-strong)] bg-transparent px-4 text-sm font-[var(--font-body)] placeholder:text-[var(--color-fg-subtle)] focus-visible:border-[var(--color-accent)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={isPending}
          className={cn(
            'type-nav inline-flex h-12 items-center justify-center gap-2 rounded-sm px-8 transition-colors',
            isPending
              ? 'cursor-not-allowed bg-[var(--color-border)] text-[var(--color-fg-subtle)]'
              : 'bg-[var(--color-accent)] text-[var(--color-accent-fg)] hover:bg-[var(--color-accent)]'
          )}
        >
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Suscribir
        </button>
      </form>

      {/* Honeypot-ish campo oculto — no es parte de la semántica de submit, pero si
          un bot lo llena, el server action lo ignorará porque el schema solo pide email.
          Lo dejamos como firstName opcional para mejor personalización. */}
      {!compact ? (
        <div className={cn('mt-3', 'flex flex-col gap-2 sm:flex-row sm:justify-center')}>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Nombre (opcional)"
            className="h-10 w-full max-w-xs rounded-sm border border-[var(--color-border)] bg-transparent px-3 text-sm placeholder:text-[var(--color-fg-subtle)] focus-visible:border-[var(--color-accent)] focus-visible:outline-none"
          />
        </div>
      ) : null}

      {showTarotOptIn ? (
        <label
          className={cn(
            'mt-4 flex cursor-pointer items-center gap-2 text-[var(--color-fg-muted)]',
            !compact && 'justify-center'
          )}
        >
          <input
            type="checkbox"
            checked={tarotOptIn}
            onChange={(e) => setTarotOptIn(e.target.checked)}
            className="accent-[var(--color-accent)]"
          />
          <span className="type-caption">También quiero la carta de tarot semanal</span>
        </label>
      ) : null}

      <p className="type-caption mt-4 text-[var(--color-fg-subtle)]">
        Al suscribirte aceptas nuestro{' '}
        <Link href="/legal/aviso-de-privacidad" className="underline underline-offset-2">
          aviso de privacidad
        </Link>
        . Puedes darte de baja en cualquier momento.
      </p>
    </div>
  );
}
